import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../services/api';

const ReportPrintView = () => {
  const { id } = useParams();
  const [report, setReport] = useState(null);
  const [sections, setSections] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/reports/${id}`)
      .then(res => {
        setReport(res.data.report);
        setSections(res.data.sections);
        setLoading(false);
      })
      .catch(() => {
        setError('Failed to load report.');
        setLoading(false);
      });
  }, [id]);

  const handlePrint = () => window.print();

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', fontFamily: 'Arial, sans-serif' }}>
      <p>Loading report...</p>
    </div>
  );

  if (error) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', fontFamily: 'Arial, sans-serif' }}>
      <p style={{ color: 'red' }}>{error}</p>
    </div>
  );

  const r = report;
  const fmt = (val) => val ?? '—';
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-PK', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
  const fmtTime = (t) => t ? new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—';

  return (
    <>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { margin: 0; }
          .page { box-shadow: none !important; margin: 0 !important; border-radius: 0 !important; }
        }
        body { background: #f3f4f6; font-family: Arial, sans-serif; }
        .page {
          max-width: 900px; margin: 30px auto; background: #fff;
          padding: 40px; box-shadow: 0 2px 12px rgba(0,0,0,0.12); border-radius: 8px;
        }
        h1 { font-size: 20px; color: #1e3a5f; margin: 0 0 4px; }
        .subtitle { font-size: 13px; color: #6b7280; margin: 0 0 24px; }
        .section-title {
          font-size: 14px; font-weight: bold; color: #1e3a5f;
          border-bottom: 2px solid #1e3a5f; padding-bottom: 4px; margin: 24px 0 12px;
          text-transform: uppercase; letter-spacing: 0.5px;
        }
        .grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 24px; }
        .grid3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 8px 24px; }
        .field label { font-size: 11px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.4px; }
        .field p { font-size: 13px; color: #111; margin: 2px 0 0; font-weight: 500; }
        table { width: 100%; border-collapse: collapse; font-size: 12px; margin-bottom: 8px; }
        th { background: #1e3a5f; color: #fff; padding: 6px 8px; text-align: left; font-size: 11px; }
        td { padding: 5px 8px; border-bottom: 1px solid #e5e7eb; }
        tr:nth-child(even) td { background: #f9fafb; }
        .badge {
          display: inline-block; padding: 3px 10px; border-radius: 999px;
          font-size: 11px; font-weight: bold;
        }
        .badge-submitted { background: #d1fae5; color: #065f46; }
        .print-btn {
          display: block; margin: 0 auto 24px; padding: 10px 32px;
          background: #1e3a5f; color: #fff; border: none; border-radius: 6px;
          font-size: 14px; font-weight: bold; cursor: pointer;
        }
        .print-btn:hover { background: #2d5282; }
        .header-row { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 8px; }
        .logo-area { text-align: right; font-size: 11px; color: #6b7280; }
      `}</style>

      <div className="no-print" style={{ textAlign: 'center', padding: '20px 0 0' }}>
        <button className="print-btn" onClick={handlePrint}>Print / Save as PDF</button>
      </div>

      <div className="page">
        <div className="header-row">
          <div>
            <h1>Performance Monitoring Report</h1>
            <p className="subtitle">Government of Sindh — Field Monitoring and Assessment System</p>
          </div>
          <div className="logo-area">
            <span className="badge badge-submitted">{r.status}</span><br />
            PMR No: {fmt(r.pmr_no)}<br />
            Book No: {fmt(r.book_no)}
          </div>
        </div>

        {/* Section 1: Visit Info */}
        <div className="section-title">1. Visit Information</div>
        <div className="grid3">
          <div className="field"><label>Officer Name</label><p>{fmt(r.officer_name)}</p></div>
          <div className="field"><label>Institution</label><p>{fmt(r.institution_name)}</p></div>
          <div className="field"><label>District</label><p>{fmt(r.district_name)}</p></div>
          <div className="field"><label>Visit Date</label><p>{fmtDate(r.visit_date)}</p></div>
          <div className="field"><label>Start Time</label><p>{fmtTime(r.start_time)}</p></div>
          <div className="field"><label>End Time</label><p>{fmtTime(r.end_time)}</p></div>
          <div className="field"><label>Training Phase</label><p>{fmt(r.phase_name)}</p></div>
          <div className="field"><label>Training Sector</label><p>{fmt(r.training_sector)}</p></div>
          <div className="field"><label>Wing</label><p>{fmt(r.wing)}</p></div>
          <div className="field"><label>Training Month No.</label><p>{fmt(r.training_month_number)}</p></div>
          <div className="field"><label>Visits This Month</label><p>{fmt(r.visits_this_month)}</p></div>
          <div className="field"><label>Total Visits So Far</label><p>{fmt(r.total_visits_so_far)}</p></div>
        </div>

        {/* Section 2: Attendance */}
        {sections?.attendance?.length > 0 && (
          <>
            <div className="section-title">2. Trainee Attendance</div>
            <table>
              <thead>
                <tr>
                  <th>Course</th>
                  <th>Trainees Enrolled</th>
                  <th>Present (Date)</th>
                  <th>Present (Visit)</th>
                  <th>Absent</th>
                  <th>Stipend Received</th>
                  <th>Displaying ID</th>
                  <th>Tutors Present</th>
                </tr>
              </thead>
              <tbody>
                {sections.attendance.map((a, i) => (
                  <tr key={i}>
                    <td>{a.course_name}</td>
                    <td>{a.number_of_trainees}</td>
                    <td>{a.trainees_present_on_date}</td>
                    <td>{a.trainees_present_current_visit}</td>
                    <td>{a.trainees_absent}</td>
                    <td>{a.trainees_received_stipend}</td>
                    <td>{a.trainees_displaying_id}</td>
                    <td>{a.tutors_present}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {/* Section 3: Academic Progress */}
        {sections?.academic?.length > 0 && (
          <>
            <div className="section-title">3. Academic Progress</div>
            <table>
              <thead>
                <tr>
                  <th>Course</th>
                  <th>Weeks Completed</th>
                  <th>Current Topic</th>
                  <th>Progress %</th>
                  <th>Remaining %</th>
                  <th>Activity Plan</th>
                  <th>Training Manual</th>
                </tr>
              </thead>
              <tbody>
                {sections.academic.map((a, i) => (
                  <tr key={i}>
                    <td>{a.course_name}</td>
                    <td>{a.weeks_completed}</td>
                    <td>{fmt(a.current_topic)}</td>
                    <td>{a.progress_completed_percent}%</td>
                    <td>{a.progress_remaining_percent}%</td>
                    <td>{a.activity_plan_available ? 'Yes' : 'No'}</td>
                    <td>{a.training_manual_available ? 'Yes' : 'No'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {/* Section 4: Examination */}
        {sections?.examination?.length > 0 && (
          <>
            <div className="section-title">4. Examination</div>
            <table>
              <thead>
                <tr>
                  <th>Course</th>
                  <th>Tests Planned</th>
                  <th>Tests Conducted</th>
                  <th>Trainees Appeared</th>
                  <th>Trainees Passed</th>
                </tr>
              </thead>
              <tbody>
                {sections.examination.map((e, i) => (
                  <tr key={i}>
                    <td>{e.course_name}</td>
                    <td>{e.tests_planned}</td>
                    <td>{e.tests_conducted}</td>
                    <td>{e.trainees_appeared}</td>
                    <td>{e.trainees_passed}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {/* Section 5: Facilities */}
        {sections?.facilities?.length > 0 && (
          <>
            <div className="section-title">5. Facilities Assessment</div>
            <table>
              <thead>
                <tr>
                  <th>Course</th>
                  <th>Tutor Capacity</th>
                  <th>Training Manual</th>
                  <th>Training Material</th>
                  <th>Learning Climate</th>
                  <th>Facilities</th>
                  <th>Grand Total</th>
                </tr>
              </thead>
              <tbody>
                {sections.facilities.map((f, i) => (
                  <tr key={i}>
                    <td>{f.course_name}</td>
                    <td>{f.tutor_capacity_score}/5</td>
                    <td>{f.training_manual_score}/5</td>
                    <td>{f.training_material_score}/5</td>
                    <td>{f.learning_climate_score}/5</td>
                    <td>{f.facilities_score}/5</td>
                    <td><strong>{f.grand_total}/25</strong></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {/* Section 6: Assessment & Recommendations */}
        <div className="section-title">6. Overall Assessment & Recommendations</div>
        <div className="grid2">
          <div className="field">
            <label>Overall Assessment</label>
            <p style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>{fmt(r.overall_assessment)}</p>
          </div>
          <div className="field">
            <label>Recommendations</label>
            <p style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>{fmt(r.recommendations)}</p>
          </div>
        </div>

        {/* Signatures */}
        <div className="section-title">7. Signatures</div>
        <div className="grid2" style={{ marginTop: 32 }}>
          <div style={{ textAlign: 'center' }}>
            {r.officer_signature_url && (
              <img src={r.officer_signature_url} alt="Officer Signature" style={{ maxHeight: 60, marginBottom: 4 }} />
            )}
            <div style={{ borderTop: '1px solid #374151', paddingTop: 4, fontSize: 12, color: '#374151' }}>
              Officer Signature<br /><strong>{fmt(r.officer_name)}</strong>
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            {r.focal_person_signature_url && (
              <img src={r.focal_person_signature_url} alt="Focal Person Signature" style={{ maxHeight: 60, marginBottom: 4 }} />
            )}
            <div style={{ borderTop: '1px solid #374151', paddingTop: 4, fontSize: 12, color: '#374151' }}>
              Focal Person Signature
            </div>
          </div>
        </div>

        <div style={{ marginTop: 32, fontSize: 11, color: '#9ca3af', textAlign: 'center', borderTop: '1px solid #e5e7eb', paddingTop: 12 }}>
          Submitted: {fmtDate(r.submitted_at)} {fmtTime(r.submitted_at)} &nbsp;|&nbsp; Report ID: {r.id} &nbsp;|&nbsp; e-fieldoffice.com
        </div>
      </div>
    </>
  );
};

export default ReportPrintView;
